import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { ArrecadacaoItemResponse } from "features/arrecadacoes/typings";
import { useMemo } from "react";
import { getDateSubtitle, getTypeTag } from "../util/helper";
import colors from "core/resources/theme/colors";
import InfoTooltip from "components/InfoTooltip";
import { formatMoney } from "infra/helpers/number";
import { ColumnType } from "antd/lib/table";
import { FormaDistribuicaoArrecadacaoEnum } from "features/arrecadacoes/enums";
import { AiOutlineFieldTime, AiOutlineCalendar } from "react-icons/ai";
import TagStatus from "./TagStatus/index";
import { TipoConsumoEnum } from "core/features/consumo/typings";

interface UseListagemArrecadacoesColumnsProps {
  onOpenRateioModal?: ({ id, nome }: { id: string; nome: string }) => any;
  onCloseRateio?: (id: string) => any;
}

function useListagemArrecadacoesColumns({
  onOpenRateioModal,
  onCloseRateio,
}: UseListagemArrecadacoesColumnsProps) {
  return useMemo((): ColumnType<ArrecadacaoItemResponse>[] => {
    const mapTipoConsumo: Record<TipoConsumoEnum, string> = {
      [TipoConsumoEnum.Agua]: "Consumo de água",
      [TipoConsumoEnum.AguaMineral]: "Consumo de água mineral",
      [TipoConsumoEnum.Gas]: "Consumo de gás",
      [TipoConsumoEnum.Energia]: "Consumo de energia",
      [TipoConsumoEnum.Esgoto]: "Consumo de esgoto",
    };

    const mapUnidadeMedida: Record<TipoConsumoEnum, string> = {
      [TipoConsumoEnum.Agua]: "L",
      [TipoConsumoEnum.Esgoto]: "L",
      [TipoConsumoEnum.AguaMineral]: "L",
      [TipoConsumoEnum.Gas]: "m³",
      [TipoConsumoEnum.Energia]: "kWh",
    };

    return [
      {
        title: "Descrição",
        render: (original: ArrecadacaoItemResponse) => (
          <Flex flexDir="column">
            <Text fontSize="12px">
              {original.formaDistribuicaoArrecadacao.id ===
              FormaDistribuicaoArrecadacaoEnum.UnicaUnidade
                ? "Para uma unidade "
                : null}
              {original.formaDistribuicaoArrecadacao.id ===
                FormaDistribuicaoArrecadacaoEnum.Consumo &&
              original.tipoConsumoId
                ? mapTipoConsumo[original.tipoConsumoId]
                : original.tipoArrecadacao?.nome}
            </Text>

            <Flex gap="4px" alignItems="center">
              <AiOutlineCalendar />
              <Text fontSize="12px" color={colors.black} fontWeight="bold">
                {getDateSubtitle(original.dataInicio, original.dataFim)}
              </Text>
            </Flex>
          </Flex>
        ),
      },
      {
        title: "Vencimento",
        render: (original: ArrecadacaoItemResponse) => (
          <Text fontSize="12px">Dia {original.diaVencimento}</Text>
        ),
      },
      {
        title: "Forma de arrecadação",
        render: (original: ArrecadacaoItemResponse) => (
          <Flex
            gap="4px"
            color={colors.black}
            fontWeight="medium"
            align="center"
          >
            {original.valor ? (
              <Flex>{formatMoney(original.valor)}</Flex>
            ) : original.percentualRateio ? (
              `${original.percentualRateio}%`
            ) : null}
            <Text
              fontSize="12px"
              color="gray.700"
              fontWeight="light"
              display="inline"
            >
              {original.formaDistribuicaoArrecadacao.id ===
                FormaDistribuicaoArrecadacaoEnum.Consumo &&
              original.tipoConsumoId
                ? `/ ${mapUnidadeMedida[original.tipoConsumoId]}`
                : `(${original.formaDistribuicaoArrecadacao.nome})`}
            </Text>
            {original.rateioAberto && original.rateio ? (
              <Box as="span" ml={1}>
                <InfoTooltip
                  text="Esse rateio ainda está em aberto para lançamento de despesas."
                  primaryButtonOnClick={() =>
                    onOpenRateioModal?.(original.rateio!)
                  }
                  closeButtonOnClick={() =>
                    onCloseRateio?.(original.rateio?.id!)
                  }
                  primaryButtonText="Ver rateio"
                />
              </Box>
            ) : null}
          </Flex>
        ),
      },
      {
        title: "",
        render: (original: ArrecadacaoItemResponse) => (
          <Flex gap="8px" whiteSpace="nowrap" wrap="wrap">
            <TagStatus colorScheme="orange">
              {original.categoriaFinanceira.nome}
            </TagStatus>

            {original.reserva ? (
              <TagStatus colorScheme={"gray"}>Reserva</TagStatus>
            ) : null}

            <TagStatus colorScheme={original.extra ? "blue" : "green"}>
              {getTypeTag(original.extra)}
            </TagStatus>

            {!original.processada ? (
              <TagStatus colorScheme="purple">
                <AiOutlineFieldTime /> Não processada
              </TagStatus>
            ) : null}
          </Flex>
        ),
      },
    ];
  }, [onCloseRateio, onOpenRateioModal]);
}

export default useListagemArrecadacoesColumns;
